/**
 * Tâche 1273 (Eric 2026-06-29) — Diagnostic de fusion dans une vraie fenêtre.
 *
 * Le bouton « Diagnostic fusion » ouvrait un `window.alert` : une boîte
 * système où l'on lisait `fusion_sources: {"whisper":...}` en JSON brut. Et la
 * même information vivait à moitié dans un bandeau dépliable au-dessus de la
 * transcription, qui mangeait de la place à l'écran.
 * Tout est réuni ici, dans la même fenêtre contextuelle que « Réglages
 * fusion » ; le bandeau a été retiré de la page.
 */

import { createPortal } from "react-dom";

const mmss = (s) => {
  if (s === null || s === undefined) return "?";
  const t = Math.max(0, Math.floor(Number(s) || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};

const Stat = ({ label, value, testid, strong }) => (
  <div className="bg-white rounded p-2 border border-blue-100">
    <p className="font-semibold text-blue-900 text-[11px]">{label}</p>
    <p className={`leading-tight ${strong ? "text-blue-700 font-bold text-lg" : "text-zinc-700 text-[11px]"}`} data-testid={testid}>
      {value}
    </p>
  </div>
);

export default function FusionDiagnosticModal({ open, onClose, merged, lang = "fr" }) {
  if (!open) return null;

  const m = merged || {};
  const d = m.fusion_diagnostic || {};
  const segs = m.segments || [];
  const total = segs.reduce((n, s) => n + ((s.corrections || []).length), 0);
  const accepted = segs.reduce((n, s) => n + ((s.corrections || []).filter((c) => c.accepted).length), 0);
  const rejected = segs.reduce((n, s) => n + ((s.corrections || []).filter((c) => c.rejected).length), 0);
  const speakers = (d.distinct_final_speakers || []).join(", ");

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="fusion-diagnostic-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-zinc-200 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
            <span aria-hidden="true">📊</span>
            {lang === "fr" ? "Diagnostic de fusion" : "Fusion diagnostic"}
            <span className="text-[11px] font-normal text-zinc-500">
              {lang === "fr" ? "règle Fireflies-first" : "Fireflies-first rule"}
              {d.meeting_kind ? ` · ${String(d.meeting_kind).replace(/_/g, " ")}` : ""}
            </span>
          </h2>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 text-xl leading-none"
            data-testid="fusion-diagnostic-close-btn"
            aria-label="close"
          >×</button>
        </div>

        <div className="p-4 space-y-4">
          {/* Ce que disait le bandeau retiré : quelle méthode a produit la fusion. */}
          <section data-testid="fusion-diagnostic-method">
            <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">
              {lang === "fr" ? "Méthode appliquée" : "Applied method"}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat
                strong
                label={lang === "fr" ? "Méthode timecode (Fireflies)" : "Timecode method (Fireflies)"}
                testid="fusion-diag-timecode-count"
                value={<>
                  {d.timecode_overlap_method_count || 0}
                  <span className="text-[10px] text-blue-700/60 font-normal ml-1">/ {d.total_segments || 0}</span>
                </>}
              />
              <Stat
                strong
                label={lang === "fr" ? "Méthode texte (fallback)" : "Text method (fallback)"}
                value={d.text_overlap_method_count || 0}
              />
              <Stat
                strong
                label={lang === "fr" ? "Conservé (Whisper)" : "Unchanged (Whisper)"}
                value={d.unchanged_method_count || 0}
              />
              <Stat
                label={lang === "fr" ? "Locuteurs finaux" : "Final speakers"}
                testid="fusion-diag-speakers"
                value={speakers || (lang === "fr" ? "(aucun)" : "(none)")}
              />
            </div>
            {d.fireflies_segments_available === false && (
              <p className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded" data-testid="fusion-diagnostic-no-timecodes">
                {lang === "fr"
                  ? "⚠ Fireflies sans timecodes structurés — la fusion a utilisé uniquement le fallback texte. Réimportez Fireflies au format avec horodatages pour activer la règle Fireflies-first."
                  : "⚠ Fireflies has no structured timecodes — merge used text fallback only."}
              </p>
            )}
          </section>

          {/* Ce que disait la boîte système, rendu lisible. */}
          <section data-testid="fusion-diagnostic-internals">
            <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">
              {lang === "fr" ? "État interne de la fusion" : "Merge internals"}
            </h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
              <dt className="text-zinc-500">{lang === "fr" ? "Segments fusionnés" : "Merged segments"}</dt>
              <dd className="text-zinc-900 tabular-nums" data-testid="fusion-diagnostic-segments">{segs.length}</dd>
              <dt className="text-zinc-500">{lang === "fr" ? "Taille du texte" : "Text size"}</dt>
              <dd className="text-zinc-900 tabular-nums">
                {(m.merged_text || "").length.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")} {lang === "fr" ? "caractères" : "chars"}
              </dd>
              <dt className="text-zinc-500">{lang === "fr" ? "Dernière fusion" : "Last merge"}</dt>
              <dd className="text-zinc-900 text-[11px]">
                {m.merged_at ? new Date(m.merged_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA") : (lang === "fr" ? "inconnue" : "unknown")}
              </dd>
              <dt className="text-zinc-500">{lang === "fr" ? "Rapport de fusion" : "Fusion report"}</dt>
              <dd className="text-zinc-900">
                {m.fusion_report ? (lang === "fr" ? "présent" : "present") : (lang === "fr" ? "absent" : "absent")}
              </dd>
              <dt className="text-zinc-500">{lang === "fr" ? "Corrections" : "Corrections"}</dt>
              <dd className="text-zinc-900 tabular-nums" data-testid="fusion-diagnostic-corrections">
                {total} ({accepted} {lang === "fr" ? "acceptées" : "accepted"} · {rejected} {lang === "fr" ? "refusées" : "rejected"} · {Math.max(0, total - accepted - rejected)} {lang === "fr" ? "en attente" : "pending"})
              </dd>
              {d.merged_coverage_ratio != null && (
                <>
                  <dt className="text-zinc-500">{lang === "fr" ? "Couverture" : "Coverage"}</dt>
                  <dd className="text-zinc-900 tabular-nums">
                    {Math.round(d.merged_coverage_ratio * 100)} % · {mmss(d.merged_last_end_sec)} / {mmss(d.source_last_end_sec)}
                  </dd>
                </>
              )}
            </dl>
            {m.fusion_sources && (
              <div className="mt-2">
                <p className="text-[11px] font-semibold text-zinc-600 mb-1">
                  {lang === "fr" ? "Sources de la fusion" : "Merge sources"}
                </p>
                <ul className="text-[11px] text-zinc-700 space-y-0.5" data-testid="fusion-diagnostic-sources">
                  {Object.entries(m.fusion_sources).map(([k, v]) => (
                    <li key={k}>
                      <span className="text-zinc-500">{k}</span> : {typeof v === "object" ? JSON.stringify(v) : String(v)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </div>

        <div className="p-3 border-t border-zinc-200 bg-zinc-50 flex justify-end">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-100"
            data-testid="fusion-diagnostic-footer-close-btn"
          >
            {lang === "fr" ? "Fermer" : "Close"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
